import React, { useState, useEffect } from 'react';
import AdminBackLink from '../components/AdminBackLink';

import axiosInstance from '../services/api';
import api from '../config/api';
import { Location } from '../types';
import { HiOutlinePencil, HiOutlineTrash, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_URL = API_BASE.replace(/\/api\/?$/, '');

const Locations: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    titleAr: '',
    titleEn: '',
    locationAr: '',
    locationEn: '',
    image: null as File | null,
    orderIndex: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(api.locations.getAll());
      const data = response.data;
      setLocations(Array.isArray(data?.locations) ? data.locations : []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحميل المواقع');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (loc: Location) => {
    setEditingLocation(loc);
    setFormData({
      titleAr: loc.titleAr,
      titleEn: loc.titleEn,
      locationAr: loc.locationAr,
      locationEn: loc.locationEn,
      image: null,
      orderIndex: loc.orderIndex,
      isActive: loc.isActive,
    });
    setImagePreview(loc.image ? `${API_URL}${loc.image}` : null);
    setShowForm(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الموقع؟')) return;
    try {
      setError('');
      setSuccess('');
      await axiosInstance.delete(api.locations.delete(id));
      setSuccess('تم حذف الموقع بنجاح');
      fetchLocations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل حذف الموقع');
    }
  };

  const handleToggleActive = async (loc: Location) => {
    try {
      setError('');
      setSuccess('');
      const fd = new FormData();
      fd.append('isActive', String(!loc.isActive));
      fd.append('titleAr', loc.titleAr);
      fd.append('titleEn', loc.titleEn);
      fd.append('locationAr', loc.locationAr);
      fd.append('locationEn', loc.locationEn);
      fd.append('orderIndex', String(loc.orderIndex));
      await axiosInstance.put(api.locations.update(loc.id), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('تم تحديث حالة الموقع بنجاح');
      fetchLocations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحديث الحالة');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const fd = new FormData();
      fd.append('titleAr', formData.titleAr);
      fd.append('titleEn', formData.titleEn);
      fd.append('locationAr', formData.locationAr);
      fd.append('locationEn', formData.locationEn);
      fd.append('orderIndex', String(formData.orderIndex));
      fd.append('isActive', String(formData.isActive));
      if (formData.image) fd.append('image', formData.image);

      if (editingLocation) {
        await axiosInstance.put(api.locations.update(editingLocation.id), fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSuccess('تم تحديث الموقع بنجاح');
      } else {
        await axiosInstance.post(api.locations.create, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSuccess('تم إضافة الموقع بنجاح');
      }

      setShowForm(false);
      setEditingLocation(null);
      setImagePreview(null);
      setFormData({
        titleAr: '',
        titleEn: '',
        locationAr: '',
        locationEn: '',
        image: null,
        orderIndex: 0,
        isActive: true,
      });
      fetchLocations();
    } catch (err: any) {
      const msg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.error;
      setError(msg || `فشل ${editingLocation ? 'تحديث' : 'إضافة'} الموقع`);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingLocation(null);
    setImagePreview(null);
    setFormData({
      titleAr: '',
      titleEn: '',
      locationAr: '',
      locationEn: '',
      image: null,
      orderIndex: 0,
      isActive: true,
    });
  };

  const getImageUrl = (path?: string) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-paper)' }}>
      <nav
        style={{
          backgroundColor: '#041525',
          borderBottom: '2px solid #477CD7',
          width: '100%',
          height: '72px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full relative">
            <AdminBackLink />
            <h1 
              className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2"
              style={{
                fontFamily: 'var(--font-arabtype)',
                letterSpacing: '0.5px',
              }}
            >
              المواقع
            </h1>
            <div className="w-24" />
          </div>
        </div>
      </nav>

      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
              {success}
            </div>
          )}

          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold" style={{ color: '#ffffff' }}>قائمة المواقع</h2>
            <button
              onClick={() => { handleCancel(); setShowForm(true); }}
              className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
              style={{ background: 'var(--button-gradient)' }}
            >
              إضافة موقع جديد
            </button>
          </div>

          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {editingLocation ? 'تعديل الموقع' : 'إضافة موقع جديد'}
                    </h2>
                    <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600 text-2xl">
                      ×
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">الصورة</label>
                      <div className="flex items-center gap-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:cursor-pointer file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {imagePreview && (
                          <img src={imagePreview} alt="معاينة" className="w-24 h-24 object-cover rounded-lg" />
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">العنوان بالعربية *</label>
                        <input
                          type="text"
                          value={formData.titleAr}
                          onChange={(e) => setFormData({ ...formData, titleAr: e.target.value })}
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                          style={{ borderColor: 'var(--border-main)' } as React.CSSProperties}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">العنوان بالإنجليزية *</label>
                        <input
                          type="text"
                          value={formData.titleEn}
                          onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                          style={{ borderColor: 'var(--border-main)' } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">الموقع بالعربية *</label>
                        <input
                          type="text"
                          value={formData.locationAr}
                          onChange={(e) => setFormData({ ...formData, locationAr: e.target.value })}
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                          style={{ borderColor: 'var(--border-main)' } as React.CSSProperties}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">الموقع بالإنجليزية *</label>
                        <input
                          type="text"
                          value={formData.locationEn}
                          onChange={(e) => setFormData({ ...formData, locationEn: e.target.value })}
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                          style={{ borderColor: 'var(--border-main)' } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">ترتيب العرض</label>
                      <input
                        type="number"
                        value={formData.orderIndex}
                        onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                        style={{ borderColor: 'var(--border-main)' } as React.CSSProperties}
                      />
                    </div>

                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="mr-2 text-sm font-medium text-gray-700">نشط</span>
                    </label>

                    <div className="flex gap-4 justify-end pt-4">
                      <button type="button" onClick={handleCancel} className="px-6 py-2 border rounded-lg hover:bg-gray-50">
                        إلغاء
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
                        style={{ background: 'var(--button-gradient)' }}
                      >
                        {editingLocation ? 'تحديث' : 'إضافة'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>جاري التحميل...</div>
          ) : locations.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>لا توجد مواقع</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locations.map((loc) => {
                const imgUrl = getImageUrl(loc.image);
                return (
                  <div
                    key={loc.id}
                    className="rounded-lg overflow-hidden transition-all"
                    style={{
                      background: 'rgba(8, 42, 73, 0.6)',
                      border: '1px solid transparent',
                      color: '#ffffff',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {imgUrl ? (
                      <img src={imgUrl} alt={loc.titleAr} className="w-full h-48 object-cover" />
                    ) : (
                      <div
                        className="w-full h-48 flex items-center justify-center"
                        style={{ background: 'rgba(255, 255, 255, 0.05)' }}
                      >
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>لا صورة</span>
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#ffffff' }}>{loc.titleAr}</h3>
                      <p className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{loc.locationAr}</p>
                      <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            loc.isActive ? 'bg-green-500 bg-opacity-20 text-green-300' : 'bg-gray-500 bg-opacity-20 text-gray-300'
                          }`}
                        >
                          {loc.isActive ? 'نشط' : 'غير نشط'}
                        </span>
                        <div className="flex gap-2 items-center">
                          <button
                            onClick={() => handleEdit(loc)}
                            className="p-2 rounded-lg transition-all hover:bg-blue-500 hover:bg-opacity-20"
                            style={{ color: '#477CD7' }}
                            title="تعديل"
                          >
                            {React.createElement(HiOutlinePencil as any, { size: 18 })}
                          </button>
                          <button
                            onClick={() => handleToggleActive(loc)}
                            className="p-2 rounded-lg transition-all hover:bg-yellow-500 hover:bg-opacity-20"
                            style={{ color: loc.isActive ? '#F59E0B' : '#10B981' }}
                            title={loc.isActive ? 'تعطيل' : 'تفعيل'}
                          >
                            {loc.isActive 
                              ? React.createElement(HiOutlineEyeOff as any, { size: 18 })
                              : React.createElement(HiOutlineEye as any, { size: 18 })
                            }
                          </button>
                          <button
                            onClick={() => handleDelete(loc.id)}
                            className="p-2 rounded-lg transition-all hover:bg-red-500 hover:bg-opacity-20"
                            style={{ color: '#EF4444' }}
                            title="حذف"
                          >
                            {React.createElement(HiOutlineTrash as any, { size: 18 })}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Locations;

